'use client';

import * as React from 'react';
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from 'lucide-react';
import { DayButton, DayPicker, getDefaultClassNames } from 'react-day-picker';

import { cn } from '@/lib/utils';
import { Button, buttonVariants } from '@/components/ui/button';

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'label',
  buttonVariant = 'ghost',
  formatters,
  components,
  ...props
}: React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: React.ComponentProps<typeof Button>['variant'];
}) {
  const defaultClassNames = getDefaultClassNames();

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(
        'bg-ds-surface-white group/calendar p-3 [--cell-size:2.25rem] text-sm text-ds-text-default [[data-slot=card-content]_&]:bg-transparent [[data-slot=popover-content]_&]:bg-transparent',
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className
      )}
      captionLayout={captionLayout}
      formatters={{
        formatMonthDropdown: (date) =>
          date.toLocaleString('default', { month: 'short' }),
        ...formatters,
      }}
      classNames={{
        root: cn('w-fit', defaultClassNames.root),
        months: cn(
          'relative flex w-fit flex-col items-start gap-4 md:flex-row',
          defaultClassNames.months
        ),
        month: cn(
          'flex w-fit max-w-full flex-col gap-4',
          defaultClassNames.month
        ),
        nav: cn(
          'absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1',
          defaultClassNames.nav
        ),
        button_previous: cn(
          buttonVariants({ variant: buttonVariant }),
          'h-[var(--cell-size)] w-[var(--cell-size)] select-none p-0 text-ds-text-default aria-disabled:opacity-40',
          defaultClassNames.button_previous
        ),
        button_next: cn(
          buttonVariants({ variant: buttonVariant }),
          'h-[var(--cell-size)] w-[var(--cell-size)] select-none p-0 text-ds-text-default aria-disabled:opacity-40',
          defaultClassNames.button_next
        ),
        month_caption: cn(
          'flex h-[var(--cell-size)] w-full items-center justify-center px-[var(--cell-size)]',
          defaultClassNames.month_caption
        ),
        dropdowns: cn(
          'flex h-[var(--cell-size)] w-full items-center justify-center gap-1.5 text-sm font-medium text-ds-text-darker',
          defaultClassNames.dropdowns
        ),
        dropdown_root: cn(
          'has-focus:border-ds-border-default border-ds-border-subtler shadow-xs has-focus:ring-ds-border-default/50 has-focus:ring-[3px] relative rounded-md border',
          defaultClassNames.dropdown_root
        ),
        dropdown: cn(
          'bg-ds-surface-white absolute inset-0 opacity-0',
          defaultClassNames.dropdown
        ),
        caption_label: cn(
          'select-none font-medium text-ds-text-darker',
          captionLayout === 'label'
            ? 'text-sm'
            : '[&>svg]:text-ds-text-subtle flex h-8 items-center gap-1 rounded-md pl-2 pr-1 text-sm [&>svg]:size-3.5',
          defaultClassNames.caption_label
        ),
        /** v9+ uses `month_grid` (table). The old `table` key had no effect — grid stretched to full popover width. */
        month_grid: cn(
          'border-collapse border-spacing-0 text-sm table-fixed w-[calc(7*var(--cell-size))]',
          defaultClassNames.month_grid
        ),
        weekdays: cn('border-0', defaultClassNames.weekdays),
        weekday: cn(
          'h-7 p-0 text-center text-[0.7rem] font-medium uppercase text-ds-text-subtler',
          defaultClassNames.weekday
        ),
        week: cn('border-0', defaultClassNames.week),
        week_number_header: cn(
          'w-[var(--cell-size)] select-none',
          defaultClassNames.week_number_header
        ),
        week_number: cn(
          'text-ds-text-subtler select-none text-[0.8rem]',
          defaultClassNames.week_number
        ),
        day: cn(
          'group/day relative size-[var(--cell-size)] p-0 text-center align-middle select-none text-ds-text-default [&:first-child[data-selected=true]_button]:rounded-l-md [&:last-child[data-selected=true]_button]:rounded-r-md',
          defaultClassNames.day
        ),
        // brand-lightest is near-black in dark mode — unusable as a fill.
        // subtlest/subtler stay visible in both themes.
        range_start: cn(
          'bg-ds-brand-subtlest rounded-l-md',
          defaultClassNames.range_start
        ),
        range_middle: cn(
          'bg-ds-brand-subtlest rounded-none',
          defaultClassNames.range_middle
        ),
        range_end: cn(
          'bg-ds-brand-subtlest rounded-r-md',
          defaultClassNames.range_end
        ),
        today: cn(
          'rounded-md font-semibold text-ds-brand-default',
          defaultClassNames.today
        ),
        outside: cn(
          'text-ds-text-subtler aria-selected:text-ds-text-default',
          defaultClassNames.outside
        ),
        disabled: cn(
          // Keep disabled (e.g. future / maxDate) readable — rdp default 0.5
          // opacity turns day numbers into mud on dark surfaces.
          'text-ds-text-subtler opacity-100',
          defaultClassNames.disabled
        ),
        hidden: cn('invisible', defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Root: ({ className, rootRef, ...props }) => {
          return (
            <div
              data-slot="calendar"
              ref={rootRef}
              className={cn(className)}
              {...props}
            />
          );
        },
        Chevron: ({ className, orientation, ...props }) => {
          if (orientation === 'left') {
            return (
              <ChevronLeftIcon className={cn('size-4', className)} {...props} />
            );
          }

          if (orientation === 'right') {
            return (
              <ChevronRightIcon
                className={cn('size-4', className)}
                {...props}
              />
            );
          }

          return (
            <ChevronDownIcon className={cn('size-4', className)} {...props} />
          );
        },
        DayButton: CalendarDayButton,
        WeekNumber: ({ children, ...props }) => {
          return (
            <td {...props}>
              <div className="flex size-[var(--cell-size)] items-center justify-center text-center">
                {children}
              </div>
            </td>
          );
        },
        ...components,
      }}
      {...props}
    />
  );
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}: React.ComponentProps<typeof DayButton>) {
  const defaultClassNames = getDefaultClassNames();

  const ref = React.useRef<HTMLButtonElement>(null);
  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);

  return (
    <Button
      ref={ref}
      variant="ghost"
      size="icon"
      data-day={day.date.toLocaleDateString()}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={cn(
        // Base day: solid default text (not muted). Disabled stays subtler, not washed out.
        'flex h-[var(--cell-size)] w-[var(--cell-size)] min-h-[var(--cell-size)] min-w-[var(--cell-size)] flex-col items-center justify-center gap-0 rounded-md p-0 text-xs font-normal leading-none text-ds-text-default hover:bg-ds-gray-subtlest disabled:pointer-events-none disabled:bg-transparent disabled:text-ds-text-subtler disabled:opacity-100',
        // Single select
        'data-[selected-single=true]:bg-ds-brand-subtler data-[selected-single=true]:font-medium data-[selected-single=true]:text-ds-text-darker data-[selected-single=true]:hover:bg-ds-brand-subtler',
        // Range: paint the button so the band reads in dark mode (td fill alone is too weak)
        'data-[range-middle=true]:rounded-none data-[range-middle=true]:bg-ds-brand-subtlest data-[range-middle=true]:font-medium data-[range-middle=true]:text-ds-text-darker data-[range-middle=true]:hover:bg-ds-brand-subtlest',
        'data-[range-start=true]:rounded-md data-[range-start=true]:bg-ds-brand-subtler data-[range-start=true]:font-medium data-[range-start=true]:text-ds-text-darker data-[range-start=true]:hover:bg-ds-brand-subtler',
        'data-[range-end=true]:rounded-md data-[range-end=true]:bg-ds-brand-subtler data-[range-end=true]:font-medium data-[range-end=true]:text-ds-text-darker data-[range-end=true]:hover:bg-ds-brand-subtler',
        defaultClassNames.day_button,
        className
      )}
      {...props}
    />
  );
}

export { Calendar };
